import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  message,
  duration = 5000,
  onClose
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          container: 'bg-green-50 border-green-200',
          icon: 'text-green-600',
          text: 'text-green-900',
          Icon: CheckCircle
        };
      case 'error':
        return {
          container: 'bg-red-50 border-red-200',
          icon: 'text-red-600',
          text: 'text-red-900',
          Icon: XCircle
        };
      case 'warning':
        return {
          container: 'bg-amber-50 border-amber-200',
          icon: 'text-amber-600',
          text: 'text-amber-900',
          Icon: AlertCircle
        };
      case 'info':
        return {
          container: 'bg-blue-50 border-blue-200',
          icon: 'text-blue-600',
          text: 'text-blue-900',
          Icon: Info
        };
      default:
        return {
          container: 'bg-ink-50 border-ink-200',
          icon: 'text-ink-600',
          text: 'text-ink-900',
          Icon: Info
        };
    }
  };

  const styles = getToastStyles();
  const Icon = styles.Icon;

  return (
    <div
      className={`flex items-center justify-between gap-3 p-4 border rounded-lg shadow-lg ${styles.container} animate-slide-in`}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <Icon className={`h-5 w-5 flex-shrink-0 ${styles.icon}`} />
        <p className={`text-sm font-medium ${styles.text}`}>{message}</p>
      </div>
      <button
        onClick={onClose}
        className={`p-1 hover:bg-opacity-20 hover:bg-black rounded transition-colors ${styles.icon}`}
        aria-label="Close notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default Toast;
